/**
 * useSyncEngine — single hook that drives push + pull.
 *
 * Triggers:
 *   - on mount (one-shot bootstrap)
 *   - 'online' window event
 *   - explicit requestSync() after every mutation (wired via the db router)
 *   - requestFullSync() — push then pull for the selected baby, then
 *     invalidate ['events'] (app resume, push-notification received, …)
 *   - periodic heartbeat every 5 minutes when foregrounded
 *
 * A single in-flight guard prevents stacked runs. On successful pull/push,
 * invalidate the 'events' react-query keys so the UI re-renders against the
 * fresh Dexie state, and the server-computed ['sleep-schedule'] so the
 * predicted day catches up with what was just pushed (see runSync).
 */

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { drainQueue } from './push';
import { pullEventsForBaby } from './pull';
import { registerSyncRequest } from '../db/router';
import { useAppStore } from '../../shared/stores/appStore';
import { isOffline } from '../network';

const HEARTBEAT_MS = 5 * 60 * 1000;

// Bound while useSyncEngine is mounted; a no-op otherwise (web build without
// the engine, or a call during teardown).
let fullSyncTrigger = () => Promise.resolve(null);

/**
 * Push the queue, pull the selected baby's events from the server and
 * invalidate ['events'] so every consumer (open-sleep timer, dashboard,
 * history) re-reads Dexie. Safe to call from anywhere — services, listeners,
 * plain functions. Resolves when the run (or the coalesced re-run) is done.
 */
export function requestFullSync() {
  try {
    return Promise.resolve(fullSyncTrigger());
  } catch {
    return Promise.resolve(null);
  }
}

export default function useSyncEngine() {
  const queryClient = useQueryClient();
  const selectedBabyId = useAppStore((s) => s.selectedBabyId);
  const running = useRef(false);
  const dirty = useRef(false);
  const dirtyPull = useRef(false);

  // We grab a stable reference to selectedBabyId so the trigger function
  // pulls for whatever baby is currently selected, without re-binding it
  // every render.
  const currentBabyId = useRef(selectedBabyId);
  useEffect(() => {
    currentBabyId.current = selectedBabyId;
  }, [selectedBabyId]);

  useEffect(() => {
    let cancelled = false;

    const runSync = async (opts = {}) => {
      if (cancelled) return null;
      if (running.current) {
        // Coalesce: remember that a run is wanted, and whether it needs a pull
        // (a full-sync request must not be downgraded to push-only).
        dirty.current = true;
        if (opts.pull !== false) dirtyPull.current = true;
        return null;
      }
      if (isOffline()) return null;

      running.current = true;
      let pushRes = null;
      let pullRes = null;
      try {
        pushRes = await drainQueue();
        const babyId = currentBabyId.current;
        if (opts.pull !== false && babyId) {
          try {
            pullRes = await pullEventsForBaby(babyId);
          } catch {
            // Transient network — sync engine will retry on 'online' or heartbeat.
          }
        }
        if (!cancelled && babyId && (pushRes?.processed || pullRes?.count)) {
          queryClient.invalidateQueries({ queryKey: ['events', babyId] });
        }
        // The predicted day schedule (['sleep-schedule', babyId]) is computed
        // SERVER-side from the rows the server holds, so it can only be right
        // once the push has landed. useSleepActions invalidates it the moment
        // the LOCAL Dexie write succeeds — that refetch races the drain and
        // usually loses (the POST is still queued), so the server answers with
        // a *predicted* bedtime for a night sleep that is already running; with
        // nothing refetching it afterwards (staleTime 5 min, no invalidation on
        // the heartbeat) the row sat there as "Bedtime ~21:30 · missed" under a
        // hero saying "Asleep · Night since 20:37" (v1.4.3, bug 2). Every drain
        // that moved a mutation, and every pull that brought rows (a co-parent
        // logged the sleep), therefore refreshes the schedule. The prefix
        // covers every baby: a pull can change a baby other than the selected
        // one and the drain has no per-baby breakdown. Only REAL changes
        // count: a pull always returns the newest page (`count` > 0 for any
        // baby with events), so keying on it refetched the schedule on every
        // heartbeat; `changed` is what Dexie actually had to write.
        const moved = !!(pushRes?.processed || pullRes?.changed);
        if (!cancelled && (moved || opts.invalidate)) {
          // A fetch already in flight from the optimistic invalidation was
          // sent BEFORE the push landed; on a first load react-query joins a
          // new invalidation onto it instead of cancelling (it only cancels
          // when data is cached), so its stale answer would win and clear the
          // invalidated flag. Cancel first, then refetch.
          await queryClient.cancelQueries({ queryKey: ['sleep-schedule'] });
          if (!cancelled) queryClient.invalidateQueries({ queryKey: ['sleep-schedule'] });
        }
        if (!cancelled && opts.invalidate) {
          // A full sync was explicitly requested: always re-read Dexie, even
          // when the pull carried nothing new (a row may have been removed).
          queryClient.invalidateQueries({ queryKey: ['events'] });
        }
      } finally {
        running.current = false;
        if (dirty.current && !cancelled) {
          dirty.current = false;
          const pull = dirtyPull.current;
          dirtyPull.current = false;
          // Coalesce: re-run once — pull only if a full sync was asked for
          // while we were busy (otherwise we just did one).
          runSync({ pull, invalidate: pull });
        }
      }
      return { pushRes, pullRes };
    };

    // Mount: bootstrap pull + push for current selection.
    runSync();

    // Mutation-side requestSync() lands here.
    registerSyncRequest(() => runSync({ pull: false }));

    // Service-side requestFullSync() lands here.
    fullSyncTrigger = () => runSync({ pull: true, invalidate: true });

    const onOnline = () => runSync();
    window.addEventListener('online', onOnline);

    const beat = setInterval(() => runSync(), HEARTBEAT_MS);

    return () => {
      cancelled = true;
      window.removeEventListener('online', onOnline);
      clearInterval(beat);
      registerSyncRequest(() => {});
      fullSyncTrigger = () => Promise.resolve(null);
    };
  }, [queryClient]);
}
